// 埋点工具函数

// 解析 URL 信息
export const parseUrlInfo = (url: string) => {
	try {
		// Handle empty or invalid URLs
		if (!url || typeof url !== "string") {
			return {
				host: "",
				path: "",
				path_json: "{}",
				query: "",
				query_json: "{}",
			}
		}

		// If URL is relative, try to create absolute URL with current origin
		let urlObj: URL
		if (url.startsWith("/") || url.startsWith("./") || url.startsWith("../")) {
			// Relative URL - use current origin if available
			if (typeof window !== "undefined") {
				urlObj = new URL(url, window.location.origin)
			} else {
				// Server-side - return basic path info
				return {
					host: "",
					path: url.split("?")[0],
					path_json: JSON.stringify({ path: url.split("?")[0] }),
					query: url.includes("?") ? url.split("?")[1] : "",
					query_json: JSON.stringify({}),
				}
			}
		} else {
			urlObj = new URL(url)
		}

		const queryParams = Object.fromEntries(new URLSearchParams(urlObj.search))

		return {
			host: urlObj.hostname,
			path: urlObj.pathname,
			path_json: JSON.stringify({ path: urlObj.pathname }),
			query: urlObj.search,
			query_json: JSON.stringify(queryParams),
		}
	} catch (e) {
		console.error("Error parsing URL:", e, "URL:", url)
		return {
			host: "",
			path: "",
			path_json: "{}",
			query: "",
			query_json: "{}",
		}
	}
}

// 获取当前页面信息
export const getCurrentPageInfo = () => {
	if (typeof window === "undefined") {
		return {
			url: "",
			title: "",
			referrer: "",
		}
	}

	return {
		url: window.location.href || "",
		title: document.title || "",
		referrer: document.referrer || "",
	}
}

// 获取页面浏览事件的完整属性
export const getPageViewProperties = (
	pathname: string,
	searchParams?: URLSearchParams,
) => {
	const pageInfo = getCurrentPageInfo()
	const currentUrlInfo = parseUrlInfo(pageInfo.url)
	const referrerUrlInfo = parseUrlInfo(pageInfo.referrer)

	// 处理查询参数
	const urlQueryJson = {}
	if (searchParams) {
		searchParams.forEach((value, key) => {
			urlQueryJson[key] = value
		})
	}

	return {
		// 基础页面信息
		path: pathname,
		search: searchParams ? searchParams.toString() : "",
		url: pageInfo.url,
		title: pageInfo.title,
		timestamp: new Date().toISOString(),
		time: Date.now(),
		topic: "",
		type: "page_view",

		// 当前页面 URL 信息
		url_host: currentUrlInfo.host,
		url_path: pathname,
		url_path_json: JSON.stringify({ path: pathname }),
		url_query: searchParams ? searchParams.toString() : "",
		url_query_json: JSON.stringify(urlQueryJson),

		// 来源页面信息
		referrer_url: pageInfo.referrer,
		referrer_url_host: referrerUrlInfo.host,
		referrer_url_path: referrerUrlInfo.path,
		referrer_url_path_json: referrerUrlInfo.path_json,
		referrer_url_query: referrerUrlInfo.query,
		referrer_url_query_json: referrerUrlInfo.query_json,

		// 来源信息
		source_entry_url: pageInfo.url,
		source_url: pageInfo.url,
		source_url_host: currentUrlInfo.host,
		source_url_query: searchParams ? searchParams.toString() : "",
		source_url_query_json: JSON.stringify(urlQueryJson),
	}
}

// 获取点击事件的元素信息
export const getClickElementInfo = (target: Element) => {
	const elementTag = target.tagName
	const elementId = target.id
	// 安全地获取className字符串，处理各种可能的类型
	let elementClass = ""
	try {
		if (target.className) {
			elementClass =
				typeof target.className === "string"
					? target.className
					: String(target.className)
		}
	} catch {
		elementClass = ""
	}

	const elementText = (target.textContent || "").trim().slice(0, 50)

	// 安全地构建元素信息，添加对split操作的保护
	let classSelector = ""
	if (elementClass && typeof elementClass === "string") {
		try {
			classSelector =
				"." +
				elementClass
					.split(" ")
					.filter((cls) => cls.trim())
					.join(".")
		} catch {
			classSelector = ""
		}
	}

	const elementInfo = `${elementTag}${elementId ? `#${elementId}` : ""}${classSelector}`

	return {
		element_info: elementInfo,
		element_tag: elementTag,
		element_id: elementId,
		element_class: elementClass,
		element_text: elementText,
	}
}

// 获取点击事件的完整属性
export const getClickProperties = (event: MouseEvent) => {
	const elementInfo =
		event.target instanceof Element
			? getClickElementInfo(event.target)
			: {
					element_info: "",
					element_tag: "",
					element_id: "",
					element_class: "",
					element_text: "",
				}

	return {
		...elementInfo,
		click_x: event.clientX,
		click_y: event.clientY,
		timestamp: new Date().toISOString(),
	}
}

// 获取页面离开事件的属性
export const getPageLeaveProperties = () => {
	return {
		timestamp: new Date().toISOString(),
		time: Date.now(),
	}
}
